import { Controller } from "@hotwired/stimulus";

/**
 * The "Help me choose" drawer on the pricing page. Reveals one question at a time, keeps the answer
 * trail visible, shows whichever server-rendered outcome the answers land on, and applies that
 * outcome to the pricing table behind the drawer.
 */

export default class extends Controller {
  static SELECTED_OPTION_CLASS = "wg-button-primary";
  static UNSELECTED_OPTION_CLASS = "wg-button-secondary";

  static targets = ["question", "outcome", "restart"];
  static values = { path: String };

  async open(event) {
    event?.preventDefault();

    if (this.loading) return;

    if (!this.loaded) {
      this.loading = true;

      try {
        const response = await fetch(this.pathValue, { headers: { Accept: "text/html" } });
        if (!response.ok) return;

        this.drawerContent.innerHTML = await response.text();
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    }

    this.restart();
    window.Drawer.open("#plan-finder-drawer");
  }

  get drawerContent() {
    return this.element.querySelector("#plan-finder-drawer .drawer-content");
  }

  use(event) {
    event.preventDefault();

    const outcome = event.currentTarget.closest(".plan-finder-recommendation");
    const card = this.selectableCardFor(outcome.dataset.plan);

    window.Drawer?.close("#plan-finder-drawer");

    // Enterprise renders a card but cannot be selected, and the recurrence buttons switch back to
    // the selectable plans, so it only gets the view swap.
    if (!card) {
      this.element.querySelector(".unselectable-plan-button")?.click();
      return;
    }

    this.element.querySelector(`.recurrence-btn[data-period="${outcome.dataset.recurrence}"]`)?.click();
    card.click();
    this.selectViewerTier(outcome.dataset.plan);
  }

  selectableCardFor(planName) {
    const option = this.element.querySelector(`option[data-plan="${planName}"]`);
    const card = option?.closest("[data-plans-target='planCard']") ||
                 this.element.querySelector(`[data-plans-target='planCard'][data-plan="${planName}"]`);

    return JSON.parse(card?.dataset.capabilities || "{}").selectable ? card : null;
  }

  selectViewerTier(planName) {
    const option = this.element.querySelector(`option[data-plan="${planName}"]`);
    if (!option) return;

    const select = option.closest("select");
    select.value = option.value;
    select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  answer(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const question = button.closest(".plan-finder-question");
    const nextIndex = this.questionTargets.indexOf(question) + 1;

    this.resetFrom(nextIndex);
    this.markAnswered(question, button);

    const next = button.dataset.skipRest ? null : this.questionTargets[nextIndex];

    if (next) {
      next.classList.remove("hidden");
    } else {
      this.showOutcome(this.answeredPlan(question));
    }
  }

  // So changing an earlier answer cannot leave a stale choice behind. The fallback covers accounts
  // that skip the viewer question and never click a plan.
  answeredPlan(question) {
    const answered = this.questionTargets
      .filter(target => target.classList.contains("is-previous"))
      .map(target => target.querySelector(`.plan-finder-button.${this.constructor.SELECTED_OPTION_CLASS}`)?.dataset.plan)
      .filter(Boolean);

    return answered[answered.length - 1] || question.dataset.plan;
  }

  restart(event) {
    event?.preventDefault();

    // Nothing to restart for accounts already past every Premium tier, they get the outcome only.
    if (this.questionTargets.length === 0) return;

    this.resetFrom(0);
    this.questionTargets[0].classList.remove("hidden");
  }

  // Answering an earlier question again invalidates every answer after it.
  resetFrom(index) {
    this.outcomeTargets.forEach(outcome => outcome.classList.add("hidden"));

    this.questionTargets.slice(index).forEach(question => {
      question.classList.add("hidden");
      question.classList.remove("is-previous");
      this.clearChoice(question);
    });

    if (this.hasRestartTarget) this.restartTarget.classList.add("hidden");
  }

  markAnswered(question, button) {
    question.classList.add("is-previous");
    this.clearChoice(question);
    button.classList.remove(this.constructor.UNSELECTED_OPTION_CLASS);
    button.classList.add(this.constructor.SELECTED_OPTION_CLASS);
  }

  clearChoice(question) {
    question.querySelectorAll(".plan-finder-button").forEach(button => {
      button.classList.remove(this.constructor.SELECTED_OPTION_CLASS);
      button.classList.add(this.constructor.UNSELECTED_OPTION_CLASS);
    });
  }

  showOutcome(planName) {
    this.outcomeTargets.forEach(outcome => {
      outcome.classList.toggle("hidden", outcome.dataset.plan !== planName);
    });

    if (this.hasRestartTarget) this.restartTarget.classList.remove("hidden");
  }
}
